import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { useState, type Ref } from "react";

/**
 * 一覧テーブルの描画を担う共通コンポーネント。
 *
 * TanStack Table（ヘッドレス）が列の表示/順序/幅とソート状態を持ち、ここは
 * その状態を素の table 要素に描くことに専念する。HeroUI の Table を使わないのは、
 * あちらが独自の列モデルを持っており、列幅のドラッグ変更や列固定と噛み合わせ
 * にくいため。
 *
 * 見た目は ngtg_msc（data_table_2 の PaginatedDataTable2）に合わせている:
 * ヘッダ行 56px・データ行 48px、ヘッダは白地で下線のみ、行は偶奇で
 * 白 / #F4FAFF の縞。カード（角丸・影）は呼び出し側が用意する前提。
 *
 * `meta.pinned` の列は左に固定する（data_table_2 の fixedLeftColumns 相当）。
 * 地色は行に合わせつつ、固定列の右端には区切り線を入れる。msc（data_table_2）は
 * 区切り線を出さないが、横に溢れていないと固定列だと分からないため、こちらは
 * 常に線を見せる。
 *
 * 並び替え・ページングはサーバー側で行う前提（manualSorting / manualPagination）。
 */
export default function DataTable<T>({
  table,
  isLoading = false,
  emptyMessage = "データがありません",
  onRowClick,
  onReorderRow,
  rowAppearance,
  scrollRef,
  headerFilled = false,
}: {
  table: TanstackTable<T>;
  isLoading?: boolean;
  emptyMessage?: string;
  /**
   * 行を選んだとき。セル内のボタン・リンク・入力を押したときは呼ばない
   * （インライン編集の途中で行の選択が走ると、操作を奪われるため）。
   */
  onRowClick?: (row: T) => void;
  /**
   * 行をドラッグで並べ替える。掴んだ行を、落とした行の位置へ移す。
   *
   * 掴む場所（ハンドル）は列定義側で用意し、`dataTransfer` に `getRowId` が返す
   * 行 id を入れること。行全体を掴めるようにすると、セルの中の文字を選べなく
   * なるうえ、インライン編集を始めるつもりの操作がドラッグになってしまう。
   */
  onReorderRow?: (draggedRowId: string, targetRowId: string) => void;
  /**
   * 行ごとに見た目を差し替える（見積明細の「除外」した行をグレーにするなど）。
   *
   * 地色（`background`）を列定義側ではなく表に持たせているのは、**固定列のセルが
   * 自分でも同じ地色を持つ必要がある**ため（スクロールした内容の上に重なるので、
   * 透けると下の行が見えてしまう）。縞模様の代わりに使うので、Tailwind の
   * `bg-*` を 1 つ返す。
   */
  rowAppearance?: (row: T) => { background?: string; text?: string } | undefined;
  /** 横スクロールする要素の ref（左右端のスクロールボタン用）。 */
  scrollRef?: Ref<HTMLDivElement>;
  /**
   * ヘッダ行をブランドカラーで塗りつぶす（既定は msc と同じ白地に下線）。
   *
   * **画面の見分けのためのもの**で、案件管理の一覧だけが渡している。見積管理と
   * 枠が同じで、どちらを開いているのか分からなくなるため（[[PageHeader]] の
   * アイコンと同じ目的）。
   *
   * 塗るのは**淡いブランドカラー**（``brand-100``）で、文字は白地のときと同じ
   * ものをそのまま使う。濃く塗って文字を白へ反転する案は差し戻された
   * （[[match-flutter-ui-of-ngtg-msc]]）。淡い地なら反転が要らず、
   * 見分けだけが付く。薄い色に埋もれる区切り線とつまみだけ 1 段濃くする。
   */
  headerFilled?: boolean;
}) {
  // 掴んでいる行と、落とす先として指している行。
  const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const rows = table.getRowModel().rows;
  const headerGroups = table.getHeaderGroups();
  // 表示順を反映しているのはヘッダだけ（getVisibleLeafColumns は列定義の順序の
  // まま返す）。本体のセルもこの順に並べ、ヘッダとずれないようにする。
  const headers = headerGroups.at(-1)?.headers ?? [];

  // 固定列は先頭から連続している前提で、左からの位置を積み上げる
  // （useTableSettings が並べ替え時に固定列を先頭へ寄せている）。
  const pinnedLeft = new Map<string, number>();
  let offset = 0;
  for (const header of headers) {
    if (!header.column.columnDef.meta?.pinned) break;
    pinnedLeft.set(header.column.id, offset);
    offset += header.getSize();
  }
  // 固定領域の右端。ここに区切り線を引いて、固定列の範囲を示す。
  const lastPinnedId = [...pinnedLeft.keys()].at(-1);

  // 落としたときに行が入る位置。上から下へ運んでいるなら対象の下、
  // 下から上なら対象の上に入る。線を引く辺を決めるのに使う。
  const draggingIndex = rows.findIndex((row) => row.id === draggingRowId);
  const dropIndex = rows.findIndex((row) => row.id === dropTargetId);
  const insertsAbove = draggingIndex > dropIndex;

  return (
    <div ref={scrollRef} className="h-full overflow-auto">
      {/* 列幅の合計を最小幅にし、余裕があれば広がる。狭いときは横スクロールする
          （固定幅にすると広い画面で右に余白が残り、狭い画面で列が潰れる）。
          border-collapse ではなく border-separate にしているのは、collapse だと
          固定ヘッダ・固定列の罫線が一緒にスクロールして消えてしまうため。 */}
      <table
        className="w-full border-separate border-spacing-0 text-sm"
        style={{ minWidth: table.getTotalSize() }}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.id} className="h-14">
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                const left = pinnedLeft.get(header.column.id);
                const pinned = left !== undefined;
                return (
                  <th
                    key={header.id}
                    style={{ width: header.getSize(), left }}
                    className={[
                      "sticky top-0 border-b px-4 text-left text-[13px] font-bold whitespace-nowrap",
                      headerFilled
                        ? "border-brand-300 bg-brand-100"
                        : "border-default-300 bg-white",
                      pinned ? "z-30" : "z-20",
                      header.column.id === lastPinnedId
                        ? headerFilled
                          ? "border-r border-r-brand-300"
                          : "border-r border-r-default-300"
                        : "",
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      disabled={!canSort}
                      onClick={header.column.getToggleSortingHandler()}
                      className={[
                        "group flex w-full items-center gap-1 text-left",
                        // 数値列は値を右寄せにしているので、ヘッダも合わせる。
                        header.column.columnDef.meta?.numeric ? "justify-end" : "",
                        canSort
                          ? "cursor-pointer hover:text-brand-500"
                          : "cursor-default",
                      ].join(" ")}
                    >
                      <span className="truncate">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </span>
                      {canSort &&
                        (sorted === "asc" ? (
                          <ArrowUp size={16} className="shrink-0 text-brand-500" />
                        ) : sorted === "desc" ? (
                          <ArrowDown size={16} className="shrink-0 text-brand-500" />
                        ) : (
                          // 並び替え可能な列すべてに矢印を出すと一覧がうるさく
                          // なるため、未ソート列はホバー時だけ出す（ngtg_msc は
                          // そもそも並び替え可能な列が少なく、矢印は
                          // ソート中の列にしか出ない）。
                          <ChevronsUpDown
                            size={16}
                            className={[
                              "shrink-0 opacity-0 transition-opacity group-hover:opacity-100",
                              headerFilled ? "text-brand-400" : "text-default-400",
                            ].join(" ")}
                          />
                        ))}
                    </button>

                    {/* 列幅のドラッグハンドル。掴んでいる間はブランドカラーで示す。 */}
                    {header.column.getCanResize() && (
                      <div
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`${header.column.id} の幅を変更`}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onDoubleClick={() => header.column.resetSize()}
                        className={[
                          "absolute top-0 right-0 h-full w-1.5 cursor-col-resize touch-none select-none",
                          header.column.getIsResizing()
                            ? "bg-brand-500"
                            : headerFilled
                              ? "hover:bg-brand-400"
                              : "hover:bg-brand-300",
                        ].join(" ")}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={headers.length}
                className="px-4 py-16 text-center text-default-500"
              >
                {isLoading ? "読み込み中..." : emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const cells = new Map(
                row.getVisibleCells().map((cell) => [cell.column.id, cell]),
              );
              // ngtg_msc と同じく偶奇で地色を変える（行の追いやすさ）。固定列は
              // スクロールした内容の上に重なるので、同じ地色を自分でも持つ必要がある。
              const isDragging = draggingRowId === row.id;
              // 掴んでいる行の上を通っているときは、落とす先として示さない
              // （自分の位置へ戻すだけで何も起きないため）。
              const isDropTarget = dropTargetId === row.id && !isDragging;
              const appearance = rowAppearance?.(row.original);
              // 落とす先の行は縞より優先してブランド色の地にする。行ごとの
              // 地色（除外した行など）は、その次に縞より優先する。
              const stripe = isDropTarget
                ? "bg-brand-50"
                : (appearance?.background ??
                  (index % 2 === 0 ? "bg-white" : "bg-table-stripe"));
              // 行が入る辺に線を引く。border ではなく内側の影にしているのは、
              // 枠を足すと行の高さが変わって表全体が揺れるため。
              const insertionLine = !isDropTarget
                ? ""
                : insertsAbove
                  ? "shadow-[inset_0_3px_0_0_var(--color-brand-500)]"
                  : "shadow-[inset_0_-3px_0_0_var(--color-brand-500)]";
              return (
                <tr
                  key={row.id}
                  // 掴んでいる行は薄くして、いま何を持っているかを分かるようにする。
                  style={isDragging ? { opacity: 0.4 } : undefined}
                  onDragStart={
                    onReorderRow ? () => setDraggingRowId(row.id) : undefined
                  }
                  onDragOver={
                    onReorderRow
                      ? (event) => {
                          // preventDefault しないと drop が起きない。
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                          setDropTargetId(row.id);
                        }
                      : undefined
                  }
                  onDragLeave={
                    onReorderRow
                      ? () =>
                          setDropTargetId((current) =>
                            current === row.id ? null : current,
                          )
                      : undefined
                  }
                  // 表の外で手を離したときも強調を消す（drop は起きない）。
                  onDragEnd={
                    onReorderRow
                      ? () => {
                          setDraggingRowId(null);
                          setDropTargetId(null);
                        }
                      : undefined
                  }
                  onDrop={
                    onReorderRow
                      ? (event) => {
                          event.preventDefault();
                          const dragged = event.dataTransfer.getData("text/plain");
                          setDraggingRowId(null);
                          setDropTargetId(null);
                          if (dragged && dragged !== row.id) {
                            onReorderRow(dragged, row.id);
                          }
                        }
                      : undefined
                  }
                  onClick={
                    onRowClick
                      ? (event) => {
                          // セルの中の操作（インライン編集・ボタン・リンク）を
                          // 押したときは行の選択にしない。編集を始めた瞬間に
                          // 行が選ばれると、入力欄から操作が奪われる。
                          if (
                            (event.target as HTMLElement).closest(
                              'button, a, input, select, textarea, [role="button"]',
                            )
                          ) {
                            return;
                          }
                          onRowClick(row.original);
                        }
                      : undefined
                  }
                  className={[
                    "h-12",
                    stripe,
                    appearance?.text ?? "",
                    onRowClick ? "cursor-pointer" : "",
                  ].join(" ")}
                >
                  {headers.map((header) => {
                    const cell = cells.get(header.column.id);
                    if (!cell) return null;
                    const left = pinnedLeft.get(header.column.id);
                    return (
                      <td
                        key={cell.id}
                        style={{ width: header.getSize(), left }}
                        className={[
                          "truncate border-b border-default-100 px-4",
                          insertionLine,
                          left !== undefined ? `sticky z-10 ${stripe}` : "",
                          header.column.id === lastPinnedId
                            ? "border-r border-r-default-300"
                            : "",
                          cell.column.columnDef.meta?.numeric ? "text-right" : "",
                        ].join(" ")}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
